import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    vec2,
    vec3,
} from 'gl-matrix';

export enum ContourGeometricType {
    POINT,
    OPEN_PLANAR,
    OPEN_NONPLANAR,
    CLOSED_PLANAR,
}

/**
 * {@link Contour} represents a single enclosed sequence of 3D points that are connected with lines.
 */
export class Contour implements Iterable<ReadonlyVec3> {
    /**
     * Array of {@link vec3 vectices} that make up this {@link Contour}.\
     * All of these {@link vec3 points} are unique.\
     * So the first point is not duplicated.
     */
    private readonly _vertices: vec3[];

    /**
     * The centroid of this {@link Contour}.\
     * This is the average of all the vertices stored within this {@link Contour}.\
     * Saved into a parameter as it takes time to compute and only changes when the {@link vertices} change.
     */
    private _centroid?: vec3;

    public constructor(vertices: vec3[]) {
        if (vec3.equals(vertices[0], vertices[vertices.length - 1])) {
            vertices.pop();
        }
        this._vertices = vertices;
    }

    public getVertex(index: number): ReadonlyVec3 {
        return this._vertices[index];
    }

    public get vertices(): ReadonlyArray<ReadonlyVec3> {
        return this._vertices;
    }

    /**
     * Method to get the number of {@link vertices} within this {@link Contour}.
     */
    public get vertexCount(): number {
        return this._vertices.length;
    }

    /**
     * Method to get the number of edges within this {@link Contour}.\
     * As the first point should be connected to the last point, this is equal to {@link vertexCount}.
     */
    public get edgeCount(): number {
        return this._vertices.length;
    }

    /**
     * Method to get the {@link _centroid centroid} of this {@link Contour}.\
     * The {@link _centroid centroid} is recomputed if it's not yet available.
     */
    public get centroid(): ReadonlyVec3 {
        if (!this._centroid) {
            this._centroid = vec3.create();
            for (const vertex of this._vertices) {
                vec3.add(this._centroid, this._centroid, vertex);
            }
            this._centroid = vec3.scale(
                this._centroid,
                this._centroid,
                1 / this.vertexCount
            );
        }
        return this._centroid;
    }

    /**
     * Method to reverse the winding of contour data
     */
    public reverseWinding() {
        this._vertices.reverse();
    }

    /**
     * Method to correct the winding of this {@link Contour}.\
     * {@link vertices Vertices} are ordered CLOCK_WISE when inspecting from above.\
     * This has to loop through all the {@link vertices}, which is relatively expensive operation.
     * @param rotation {@link ReadonlyMat3 RotationMatrix} defining the normal direction.
     * @returns the vertices transformed onto the plane defined by the {@link rotation}.
     */
    public correctWinding(rotation: ReadonlyMat3): ReadonlyVec2[] {
        //in texture space z should stay the same for each vertex
        const textureVertex = vec3.create();
        let previous: ReadonlyVec2 | null = null;
        const planarVertices = new Array<ReadonlyVec2>();
        let sum = 0;
        for (const patientVertex of this._vertices) {
            vec3.transformMat3(textureVertex, patientVertex, rotation);
            const planarVertex = vec2.fromValues(
                textureVertex[0],
                textureVertex[1]
            );
            planarVertices.push(planarVertex);
            if (previous) {
                sum +=
                    (textureVertex[0] - previous[0]) *
                    (textureVertex[1] + previous[1]);
            }
            previous = planarVertex;
        }
        if (sum < 0) {
            this._vertices.reverse();
        }
        return planarVertices;
    }

    [Symbol.iterator](): Iterator<ReadonlyVec3> {
        return this._vertices[Symbol.iterator]();
    }
}
